"use client";

import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";

import { trackReportInputCollectionStarted } from "@/lib/analytics-agent-offers";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";

/**
 * BridgeOfferLink — the clickable unlock CTA for {@link CalculatorReportBridge}.
 *
 * Why this island exists: the bridge surface stays server-rendered (its title /
 * body / depth-teaser belong in the SSR HTML for SEO), but the CTA must fire
 * `report_input_collection_started`. This is a navigation to collect birth
 * details, deliberately not an `offer_cta_tapped` buying milestone; mixing it into
 * offer CTR previously made a bridge look like a checkout-capable surface.
 *
 * A click is a discrete user action (no re-render firing), so it fires plainly on
 * `onClick`. The navigation event keeps the same planId / placement / sourcePath /
 * intent attribution keys, without polluting offer-seen→buying-CTA-tapped analysis.
 */
export default function BridgeOfferLink({
  href,
  planId,
  sourcePath,
  intent,
  placement,
  customerProblem,
  routeRankScore,
  routeRankReason,
  rankedOfferOrder,
  presentationId,
  className,
  style,
  children,
}: {
  href: string;
  planId: string;
  sourcePath: string;
  intent: string;
  placement: string;
  customerProblem?: string;
  routeRankScore?: number;
  routeRankReason?: string;
  rankedOfferOrder?: string[];
  presentationId?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const resolvedPresentationId =
    presentationId ??
    offerPresentationId({
      actionMode: "input_collection",
      planId,
      placement,
      sourcePath,
    });
  return (
    <Link
      href={href}
      className={className}
      style={style}
      onClick={() =>
        trackReportInputCollectionStarted({
          plan_id: planId,
          source_path: sourcePath,
          intent,
          placement,
          checkout_channel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
          commerce_system: ISHVARAM_COMMERCE_SYSTEM,
          customer_problem: customerProblem,
          route_rank_score: routeRankScore,
          route_rank_reason: routeRankReason,
          ranked_offer_order: rankedOfferOrder,
          presentation_id: resolvedPresentationId,
        })
      }
    >
      {children}
    </Link>
  );
}
